import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export { formatCurrency, formatPercent } from '@jobos/shared';

/**
 * Classes for an affordance (pencil, delete icon) that fades in when its `group`
 * is hovered. A touch screen never hovers, so hiding it there leaves the control
 * invisible and effectively unreachable — it stays visible unless the device can
 * actually hover. Requires `group` on an ancestor.
 */
export const HOVER_REVEAL =
  'transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100';

/**
 * A DATE-ONLY string is anchored to LOCAL midnight; anything else is left to
 * `new Date`.
 *
 * Without the distinction, 'YYYY-MM-DD' parses as UTC midnight and renders in
 * local time, so every calendar date displayed west of UTC was a day early —
 * an invoice dated 2026-01-05 read as 1/4/2026 in the table while its own PDF
 * said January 5. Timestamps keep the old path: those carry a real instant, and
 * showing it in the reader's zone is the correct thing to do with one.
 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function formatDate(dateString: string): string {
  const date = DATE_ONLY.test(dateString)
    ? new Date(`${dateString}T00:00:00`)
    : new Date(dateString);
  return date.toLocaleDateString();
}

/**
 * Download a Response as a file. Reads the filename from Content-Disposition
 * header, falling back to the provided default.
 */
export async function downloadResponseAsFile(response: Response, fallbackFilename: string) {
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition');
  const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
  const filename = filenameMatch?.[1] ?? fallbackFilename;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}
